import { create } from 'zustand';
import { useCallback, useRef } from 'react';

/**
 * Entity types that can be displayed in breadcrumbs
 */
export type BreadcrumbEntityType = 'customer' | 'company' | 'traveler' | 'person' | 'pnr' | 'invoice' | 'trip' | 'booking' | 'supplier' | 'partner';

/**
 * Enhanced breadcrumb context for entity pages
 */
export interface BreadcrumbEntity {
  type: BreadcrumbEntityType;
  id: string;
  label: string; // Display name like "John Smith" or "INV-2024-001"
  subtitle?: string; // Optional subtitle like email or PNR locator
}

/**
 * Domain mapping for navigation hierarchy
 */
export type BreadcrumbDomain = 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';

/**
 * Breadcrumb label overrides keyed by full path.
 * Example key: "/customers/<uuid>" -> "#12345"
 *
 * Intentionally NOT persisted; this is view-state derived from loaded data.
 */
interface BreadcrumbState {
  overrides: Record<string, string>;
  entityContext: BreadcrumbEntity | null;
  currentDomain: BreadcrumbDomain | null;
  currentSection: string | null;
  setOverride: (path: string, label: string) => void;
  clearOverride: (path: string) => void;
  clearAll: () => void;
  setEntityContext: (entity: BreadcrumbEntity | null) => void;
  setCurrentContext: (domain: BreadcrumbDomain | null, section: string | null) => void;
}

export const useBreadcrumbStore = create<BreadcrumbState>((set) => ({
  overrides: {},
  entityContext: null,
  currentDomain: null,
  currentSection: null,
  setOverride: (path, label) =>
    set((state) => ({
      overrides: {
        ...state.overrides,
        [path]: label,
      },
    })),
  clearOverride: (path) =>
    set((state) => {
      if (!state.overrides[path]) return state;
      const { [path]: _removed, ...rest } = state.overrides;
      return { overrides: rest };
    }),
  clearAll: () => set({ overrides: {}, entityContext: null }),
  setEntityContext: (entity) => set({ entityContext: entity }),
  setCurrentContext: (domain, section) => set({ currentDomain: domain, currentSection: section }),
}));

/**
 * Hook to set breadcrumb context from a page component.
 *
 * Returns **stable** `setBreadcrumb` / `clearBreadcrumb` references so they
 * can safely appear in `useEffect` dependency arrays without triggering
 * infinite re-render loops (React error #185).
 */
export function useBreadcrumb(options: {
  domain?: BreadcrumbDomain;
  section?: string;
  entity?: BreadcrumbEntity;
  override?: { path: string; label: string };
}) {
  // Keep a mutable ref to the latest options so the stable callbacks always
  // read the most-recent values without needing to be recreated.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const setBreadcrumb = useCallback(() => {
    const opts = optionsRef.current;
    const store = useBreadcrumbStore.getState();
    if (opts.domain || opts.section) {
      store.setCurrentContext(opts.domain || null, opts.section || null);
    }
    if (opts.entity) {
      store.setEntityContext(opts.entity);
    }
    if (opts.override) {
      store.setOverride(opts.override.path, opts.override.label);
    }
  }, []);

  const clearBreadcrumb = useCallback(() => {
    const store = useBreadcrumbStore.getState();
    store.setEntityContext(null);
    store.setCurrentContext(null, null);
    const opts = optionsRef.current;
    if (opts.override) {
      store.clearOverride(opts.override.path);
    }
  }, []);

  return { setBreadcrumb, clearBreadcrumb };
}
